import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { LeafletMapboxService } from '../leaflet-mapbox.service';
import { environment } from '../../../environments/environment';

export interface IsochroneParams {
  profile?: 'driving' | 'walking' | 'cycling';
  minutes?: number[];
  polygons?: boolean;
  contours_colors?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class MapboxIsochroneService {
  private readonly http = inject(HttpClient);
  private readonly mapboxService = inject(LeafletMapboxService);
  private readonly apiUrl = `${environment.apiUrl}/api/geoData/getIsochrone`;
  private cache = new Map<string, any>();

  /**
   * Fetches isochrone data from Mapbox API via backend proxy.
   *
   * @param {number} lng - Longitude of the center point.
   * @param {number} lat - Latitude of the center point.
   * @param {IsochroneParams} params - Optional parameters for the isochrone request.
   * @return {Promise<any>} A promise that resolves to the GeoJSON isochrone data.
   */
  async getIsochrone(lng: number, lat: number, params: IsochroneParams = {}) {
    const profile = params.profile || 'driving';
    const polygons = params.polygons !== false;
    const token = this.mapboxService.getMapBoxToken();

    // Prepare minutes and colors, and sort them descending for correct stacking
    // (an empty array must also fall back to the default - Mapbox rejects a
    // request with no contours, which would silently fail and leave the
    // previous isochrone layer stuck on the map).
    const minutesArray =
      params.minutes && params.minutes.length > 0
        ? params.minutes
        : [15, 30, 45];
    const colorsArray = params.contours_colors || [];

    const combined = minutesArray.map((m, i) => ({
      m,
      c: colorsArray[i] || null,
    }));
    combined.sort((a, b) => b.m - a.m);

    const minutes = combined.map(item => item.m);
    const contours_colors = combined
      .map(item => item.c?.replace('#', ''))
      .filter(c => !!c)
      .join(',');

    const cacheKey = `${lng},${lat},${profile},${minutes.join(',')},${polygons}${contours_colors ? ',' + contours_colors : ''}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    const result = await lastValueFrom(
      this.http.post<any>(this.apiUrl, {
        lng,
        lat,
        profile,
        minutes: minutes.join(','),
        polygons: polygons.toString(),
        contours_colors: contours_colors || undefined,
        accessKey: token,
      })
    );

    this.cache.set(cacheKey, result);
    return result;
  }
}
